import { listQuestions } from '../graphql/queries';
import { updateQuestions } from '../graphql/mutations';
import React, { useState, useEffect } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import MuiAlert from '@material-ui/lab/Alert';
import { useNavigate } from 'react-router-dom';
import { createQuestions } from '../graphql/mutations';
import { makeStyles } from '@material-ui/core/styles';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Button,
  Checkbox,
  Radio,
  Link,
  Paper
} from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { ArrowUpward, ArrowDownward } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(6),
    },
  },
  textField: {
    margin: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '50%',
    },
  },
  uploadButton: {
    margin: theme.spacing(1),
  },
  submitButton: {
    margin: theme.spacing(1, 0),
  },
  guidanceContentItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  tableContainer: {
    boxShadow: '0px 8px 16px 0px rgba(0,0,0,0.1)',
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '15px',
    backgroundColor: '#f9f9f9'
  },
  tableHeader: {
    backgroundColor: '#01b5e2',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '1rem'
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#e5f7fb',
    },
  },
  tableCell: {
    padding: theme.spacing(1.5, 2),
    fontSize: '0.9rem'
  },
  actionButton: {
    color: '#01b5e2',
    borderColor: '#01b5e2',
    '&:hover': {
      backgroundColor: '#01b5e2',
      color: 'white',
    }
  },
  linkButton: {
    color: '#007bff',
    textDecoration: 'underline',
    cursor: 'pointer',
  }
}));

const AddQuestionForm = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const [groupUUID, setGroupUUID] = useState('');
  const [questionList, setQuestionList] = useState([]);
  const [question, setQuestion] = useState('');
  const [helpText, setHelpText] = useState('');
  const [confirmation, setConfirmation] = useState(false);
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedFirstQuestionId, setSelectedFirstQuestionId] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [editingQuestion, setEditingQuestion] = useState(null);


  const params = useParams();

  useEffect(() => {
    fetchQuestionList();
  }, []);

  const sortTable = () => {
    const qList = questionList;

    const sortedList = [...qList].sort((a, b) => {
      if (sortColumn === 'questionNumber') {
        return sortDirection === 'asc' ? a.questionNumber - b.questionNumber : b.questionNumber - a.questionNumber;
      } else if (sortColumn === 'question') {
        return sortDirection === 'asc' ? a.question.localeCompare(b.question) : b.question.localeCompare(a.question);
      } else if (sortColumn === 'helpText') {
        return sortDirection === 'asc' ? a.helpText.localeCompare(b.helpText) : b.helpText.localeCompare(a.helpText);
      } else if (sortColumn === 'deleted') {
        return sortDirection === 'asc' ? a.deleted - b.deleted : b.deleted - a.deleted;
      } else if (sortColumn === 'createdAt') {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }
      return 0;
    });

    setQuestionList(sortedList);
  }
  const fetchQuestionList = async () => {
    setGroupUUID(params.id);
  
    try {
      let allQuestions = [];
      let nextToken = null;
  
      // Loop to fetch all paginated results
      do {
        const response = await API.graphql(
          graphqlOperation(listQuestions, {
            filter: {
              questionsQuestionGroupId: {
                eq: params.id,
              },
            },
            nextToken, // Pass the nextToken for pagination
          })
        );
  
        const qList = response.data?.listQuestions?.items || [];
        allQuestions = [...allQuestions, ...qList]; // Aggregate questions
        nextToken = response.data?.listQuestions?.nextToken; // Update nextToken
      } while (nextToken);
  
      // Sort the aggregated question list based on the current sorting column and direction
      const sortedList = [...allQuestions].sort((a, b) => {
        if (sortColumn === 'questionNumber') {
          return sortDirection === 'asc'
            ? a.questionNumber.localeCompare(b.questionNumber)
            : b.questionNumber.localeCompare(a.questionNumber);
        } else if (sortColumn === 'question') {
          return sortDirection === 'asc'
            ? a.question.localeCompare(b.question)
            : b.question.localeCompare(a.question);
        } else if (sortColumn === 'helpText') {
          return sortDirection === 'asc'
            ? a.helpText.localeCompare(b.helpText)
            : b.helpText.localeCompare(a.helpText);
        } else if (sortColumn === 'deleted') {
          return sortDirection === 'asc' ? a.deleted - b.deleted : b.deleted - a.deleted;
        } else if (sortColumn === 'createdAt') {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        }
        return 0;
      });
  
      setQuestionList(sortedList);
  
      // Find the ID of the question with isFirstQuestion set to true
      const firstQuestionId = sortedList.find((question) => question.isFirstQuestion)?.id;
      setSelectedFirstQuestionId(firstQuestionId);
  
      // Find the highest questionNumber and set the default for new question
      const highestQuestionNumber = allQuestions.reduce((max, question) => Math.max(max, question.questionNumber), 0);
      setQuestionNumber(highestQuestionNumber + 1);
    } catch (error) {
      console.log(error);
    }
  };
  
  const handleCancel = () => {
    setQuestion('');
    setHelpText('');
    setEditingQuestion(null);
    setConfirmation(false);

    // Set questionNumber to max + 1
    const highestQuestionNumber = questionList.reduce((max, q) => Math.max(max, q.questionNumber), 0);
    setQuestionNumber(highestQuestionNumber + 1);
  };


  const handleEdit = (questionItem) => {
    setQuestion(questionItem.question);
    setHelpText(questionItem.helpText);
    setQuestionNumber(questionItem.questionNumber);
    setEditingQuestion(questionItem.id);
  };
  const handleSubmit = async (event) => {
    event.preventDefault();

    const input = {
      question: question,
      helpText: helpText,
      questionNumber: questionNumber,
      questionsQuestionGroupId: groupUUID,
      deleted: false,
      isFirstQuestion: false,
    };

    if (editingQuestion) {
      input.id = editingQuestion;
      try {
        await API.graphql(graphqlOperation(updateQuestions, { input }));
      } catch (error) {
        console.error(error);
      }
    } else {
      try {
        await API.graphql(graphqlOperation(createQuestions, { input }));
      } catch (error) {
        console.error(error);
      }
    }

    setConfirmation(true);
    setQuestion('');
    setHelpText('');
    setQuestionNumber(0);
    setEditingQuestion(null);
    fetchQuestionList();
  };

  const handleDeleteCheckboxChange = async (event, questionId) => {
    const checked = event.target.checked;

    try {
      // Update the `deleted` field for the corresponding question
      await API.graphql(
        graphqlOperation(updateQuestions, {
          input: {
            id: questionId,
            deleted: checked,
          },
        })
      );

      // Fetch the updated question list
      
      fetchQuestionList();
    } catch (error) {
      console.error(error);
    }
  };

  const handleRowClick = (questionId, question, helpText) => {
    navigate(`/edit-question-form/${questionId}`, {
      state: { id: questionId, question: question, helpText: helpText, groupID: groupUUID },
    });
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // If the same column is clicked again, toggle the sort direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If a new column is clicked, set it as the sort column with ascending direction
      setSortColumn(column);
      setSortDirection('asc');
    }
    sortTable();
  };

  const handleFirstQuestionSelection = async (questionId) => {

    // Update the `isFirstQuestion` field for the newly selected question
    await API.graphql(
      graphqlOperation(updateQuestions, {
        input: {
          id: questionId,
          isFirstQuestion: true,
        },
      })
    );

    // Update the `isFirstQuestion` field for the previously selected question (if any)
    if (selectedFirstQuestionId) {
      await API.graphql(
        graphqlOperation(updateQuestions, {
          input: {
            id: selectedFirstQuestionId,
            isFirstQuestion: false,
          },
        })
      );
    }

    // Update the selected first question ID
    setSelectedFirstQuestionId(questionId);

  };

  return (
    <>
      <Box className={classes.root} component="form" onSubmit={handleSubmit}>
        <Typography variant="h4" component="h1" gutterBottom>
          {editingQuestion ? 'Edit Question' : 'Add Question'}
        </Typography>
        <TextField
          className={classes.textField}
          required
          label="Question Number"
          type="number"
          value={questionNumber}
          onChange={(event) => setQuestionNumber(parseInt(event.target.value, 10))}
        />
        <TextField
          className={classes.textField}
          required
          label="Question"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
        />
        <TextField
          className={classes.textField}
          label="Help Text"
          multiline
          minRows={4}
          value={helpText}
          onChange={(event) => setHelpText(event.target.value)}
        />

        <Button
          className={classes.submitButton}
          variant="contained"
          color="primary"
          type="submit"
        >
          {editingQuestion ? 'Update' : 'Submit'}
        </Button>

        {editingQuestion && (
          <Button
            className={classes.submitButton}
            variant="contained"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        )}

        {confirmation && <MuiAlert severity="success">Success!</MuiAlert>}
      </Box>

      <Typography variant="subtitle1" style={{ margin: '20px 0' }}>
        Click on a question number or title in order to add and edit answers
      </Typography>

      <Paper className={classes.tableContainer}>

        <Table>
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell onClick={() => handleSort('questionNumber')}>
                Question Number {sortColumn === 'questionNumber' && (sortDirection === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
              </TableCell>
              <TableCell onClick={() => handleSort('question')}>
                Question {sortColumn === 'question' && (sortDirection === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
              </TableCell>
              <TableCell >
                Edit
              </TableCell>
              <TableCell onClick={() => handleSort('helpText')}>
                Help Text {sortColumn === 'helpText' && (sortDirection === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
              </TableCell>
              <TableCell onClick={() => handleSort('deleted')}>
                Deleted {sortColumn === 'deleted' && (sortDirection === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
              </TableCell>
              <TableCell>First Question</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {questionList.map((questionItem) => (
              <TableRow key={questionItem.id} className={classes.tableRow}>
                <TableCell onClick={() => handleRowClick(questionItem.id, questionItem.question, questionItem.helpText)}>
                  <Link component="span" style={{ cursor: 'pointer' }}>
                    {questionItem.questionNumber}
                  </Link>
                </TableCell>
                <TableCell onClick={() => handleRowClick(questionItem.id, questionItem.question, questionItem.helpText)}>
                  <Link component="span" style={{ cursor: 'pointer' }}>
                    {questionItem.question}
                  </Link>
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleEdit(questionItem)}
                  >
                    Edit
                  </Button>
                </TableCell>
                <TableCell>{questionItem.helpText}</TableCell>
                <TableCell>
                  <Checkbox
                    checked={questionItem.deleted}
                    onChange={(event) => handleDeleteCheckboxChange(event, questionItem.id)}
                  />
                </TableCell>
                <TableCell>
                  <Radio
                    checked={selectedFirstQuestionId === questionItem.id}
                    onChange={() => handleFirstQuestionSelection(questionItem.id)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

    </>
  );
};

export default AddQuestionForm;
